/* @flow */

import React, { useContext } from "react";
import { useHistory, useLocation, Link } from "react-router-dom";
import { StoreInfoContext } from "entrypoint/shared";
import { parseParams } from "@crossroads/location-search-string";
import Wrapper from "components/Wrapper";
import Button from "components/Button";
import OpenInNew from "@material-ui/icons/OpenInNew";

import { useTranslate } from "@awardit/react-use-translate";

import styles from "./styles.scss";
import cn from "classnames";

type StatusProps = {
  helpdeskLink: ?string,
  helpdeskTitle: ?string,
};

const NotFoundView = (): React$Node => {
  const t = useTranslate();
  return (
    <>
      <h2 className={styles.heading}>{t("404.TITLE")}</h2>
      <p>{t("404.BACK")} <Link to="/">{t("404.BACK_LINK")}</Link>?</p>
    </>
  );
};

const RenderStatus = ({
  status,
  helpdeskLink,
  helpdeskTitle,
}: { status: string } & StatusProps): React$Node => {
  const t = useTranslate();
  const location = useLocation();
  const { type } = parseParams(location.search);
  const isTextMessage = (type === "sms");

  const displayStatusInfo = (status, isTextMessage) => {
    if (status === "unsubscribe-success") {
      switch (isTextMessage) {
        case true: return t("NEWSLETTER_STATUS.UNREGISTER_SUCCESS.INFO_SMS");
        case false: return t("NEWSLETTER_STATUS.UNREGISTER_SUCCESS.INFO_EMAIL");
        default: break;
      }
    }

    if (status === "unsubscribe-failed") {
      switch (isTextMessage) {
        case true: return t("NEWSLETTER_STATUS.UNREGISTER_FAILED.INFO_SMS");
        case false: return t("NEWSLETTER_STATUS.UNREGISTER_FAILED.INFO_EMAIL");
        default: break;
      }
    }
  };

  if (status === "unsubscribe-success" || status === "unsubscribe-failed") {
    return (
      <>
        <h2 className={styles.heading}>
          {status === "unsubscribe-success" && t("NEWSLETTER_STATUS.UNREGISTER_SUCCESS.HEADING")}
          {status === "unsubscribe-failed" && t("NEWSLETTER_STATUS.UNREGISTER_FAILED.HEADING")}
        </h2>
        <p>
          <span>{displayStatusInfo(status, isTextMessage)}</span>
          <br />
          <span>{t("NEWSLETTER_STATUS.SUPPORT.INFO")}</span>
        </p>
        <Button
          variant="link"
          className={cn(styles.link, "link")}
          to={helpdeskLink ?? "/"}
        >
          {helpdeskTitle ?? t("NEWSLETTER_STATUS.SUPPORT_LINK_TITLE")}
          <OpenInNew style={{ height: "16px", width: "16px", marginLeft: "4px" }} />
        </Button>
        <p>{!isTextMessage ? t("NEWSLETTER_STATUS.INFO_EMAIL_LINK") : t("NEWSLETTER_STATUS.INFO_SMS_LINK")}</p>
      </>
    );
  }

  return <NotFoundView />;
};

const NewsletterStatusView = ({ status }: { status: ?string }): React$Node => {
  const history = useHistory();
  const { configuration: {
    helpdeskLink,
    helpdeskTitle,
  } } = useContext(StoreInfoContext);

  if (!status) {
    history.push("/");
    return null;
  }

  return (
    <Wrapper
      variant="pageWrapper"
      className={cn(styles.wrapper, "awardit-newsletterStatusView")}
    >
      <div className={styles.content}>
        <RenderStatus
          status={status}
          helpdeskLink={helpdeskLink}
          helpdeskTitle={helpdeskTitle}
        />
      </div>
    </Wrapper>
  );
};

export default NewsletterStatusView;
